import React from 'react';
import { Tooltip } from '@mui/material';

const TOOLTIP_PROPS = { enterDelay: 400, enterNextDelay: 200, placement: 'bottom' as const };

interface BtnProps {
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
  disabled?: boolean;
  title?: string;
  children?: React.ReactNode;
}

/** Generic toolbar button. `onMouseDown` with `e.preventDefault()` prevents
 *  the iframe from losing focus when the user clicks a toolbar button.
 *  `onClick` handles both mouse clicks and keyboard activation (Enter/Space).
 *  Disabled buttons are wrapped in a `<span>` so the Tooltip still fires. */
export const Btn = ({ onClick, active = false, danger = false, disabled = false, title, children }: BtnProps) => {
  const btn = (
    <button
      type='button'
      disabled={disabled}
      onMouseDown={e => e.preventDefault()}
      onClick={() => {
        if (!disabled) onClick();
      }}
      className={`hre-toolbar-btn${active ? ' active' : ''}${danger ? ' danger' : ''}`}
    >
      {children}
    </button>
  );

  if (!title) return btn;

  return (
    <Tooltip title={title} {...TOOLTIP_PROPS}>
      {disabled ? <span style={{ display: 'inline-flex' }}>{btn}</span> : btn}
    </Tooltip>
  );
};

/** 1 px vertical divider between toolbar groups. */
export const Sep = () => <div className='hre-sep flex-shrink-0' />;

interface DropdownOption {
  label: string;
  value: string;
}

interface DropdownProps {
  title?: string;
  value?: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  width?: string | number;
  onSaveSelection: () => void;
  onRestoreSelection: () => void;
}

/**
 * Styled `<select>` dropdown.
 * `onSaveSelection` / `onRestoreSelection` are called on focus and change so that
 * picking a font does not lose the user's text selection in the iframe.
 */
export const Dropdown = ({ title, value, onChange, options, width, onSaveSelection, onRestoreSelection }: DropdownProps) => {
  const select = (
    <select
      value={value}
      onMouseDownCapture={onSaveSelection}
      onFocus={onSaveSelection}
      onChange={e => {
        onRestoreSelection();
        onChange(e.target.value);
      }}
      style={{ width }}
      className='hre-select'
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );

  if (!title) return select;

  return (
    <Tooltip title={title} {...TOOLTIP_PROPS}>
      {select}
    </Tooltip>
  );
};

interface ColorWrapProps {
  title: string;
  onMouseDownCapture?: React.MouseEventHandler<HTMLDivElement>;
  children?: React.ReactNode;
}

/** Wrapper that adds a MUI Tooltip to a color-picker control block. */
export const ColorWrap = ({ title, onMouseDownCapture, children }: ColorWrapProps) => (
  <Tooltip title={title} {...TOOLTIP_PROPS}>
    <div className='hre-color-wrap' onMouseDownCapture={onMouseDownCapture}>
      {children}
    </div>
  </Tooltip>
);
